import React from 'react';
import {
  FiShield,
  FiTag,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiCalendar,
  FiChevronRight,
} from 'react-icons/fi';
import { toast } from '../../../utils/toast';

interface ProjectTicketSalesTabProps {
  isGuestUser: boolean;
  project: any;
  setSelectedEventUuid: (id: string) => void;
  setSearchEventQuery: (val: string) => void;
  setIsChangeEventModalOpen: (val: boolean) => void;
  totalTicketsSold: number;
  handleExportTicketSales: () => void;
  ticketRevenue: number;
  ticketTypesSummary: any[];
  netProfitLoss: number;
  totalSpentExpenses: number;
  activePopover: 'ticket' | 'sort' | 'filter' | 'dateRange' | 'amountSpent' | null;
  setActivePopover: (
    popover: 'ticket' | 'sort' | 'filter' | 'dateRange' | 'amountSpent' | null,
  ) => void;
  ticketFilter: string;
  setTicketFilter: (val: string) => void;
  sortBy: 'Newest' | 'Oldest' | 'Highest Amount' | 'Lowest Amount';
  setSortBy: (val: 'Newest' | 'Oldest' | 'Highest Amount' | 'Lowest Amount') => void;
  ticketSearch: string;
  setTicketSearch: (val: string) => void;
  getAmountFilterLabel: () => string | null;
  setMinSpend: (val: string) => void;
  setMaxSpend: (val: string) => void;
  getDateFilterLabel: () => string | null;
  setDateFrom: (val: string) => void;
  setDateTo: (val: string) => void;
  tempDateFrom: string;
  setTempDateFrom: (val: string) => void;
  tempDateTo: string;
  setTempDateTo: (val: string) => void;
  tempMinSpend: string;
  setTempMinSpend: (val: string) => void;
  tempMaxSpend: string;
  setTempMaxSpend: (val: string) => void;
  vipOnly: boolean;
  setVipOnly: (val: boolean) => void;
  tempVipOnly: boolean;
  setTempVipOnly: (val: boolean) => void;
  minSpend: string;
  maxSpend: string;
  dateFrom: string;
  dateTo: string;
  includeRefunded: boolean;
  setIncludeRefunded: (val: boolean) => void;
  isSalesLoading: boolean;
  filteredTicketTransactions: any[];
  ticketSalesData: any;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export const ProjectTicketSalesTab: React.FC<ProjectTicketSalesTabProps> = ({
  isGuestUser,
  project,
  setSelectedEventUuid,
  setSearchEventQuery,
  setIsChangeEventModalOpen,
  totalTicketsSold,
  handleExportTicketSales,
  ticketRevenue,
  ticketTypesSummary,
  netProfitLoss,
  totalSpentExpenses,
  activePopover,
  setActivePopover,
  ticketFilter,
  setTicketFilter,
  sortBy,
  setSortBy,
  ticketSearch,
  setTicketSearch,
  getAmountFilterLabel,
  setMinSpend,
  setMaxSpend,
  getDateFilterLabel,
  setDateFrom,
  setDateTo,
  tempDateFrom,
  setTempDateFrom,
  tempDateTo,
  setTempDateTo,
  tempMinSpend,
  setTempMinSpend,
  tempMaxSpend,
  setTempMaxSpend,
  vipOnly,
  setVipOnly,
  tempVipOnly,
  setTempVipOnly,
  minSpend,
  maxSpend,
  dateFrom,
  dateTo,
  includeRefunded,
  setIncludeRefunded,
  isSalesLoading,
  filteredTicketTransactions,
  ticketSalesData,
  page,
  setPage,
}) => {
  if (isGuestUser) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center border border-white/5 bg-[#0B0B0B]/40 rounded-3xl p-8 max-w-lg mx-auto shadow-xl">
        <FiShield className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
        <h3 className="text-white text-base font-bold">Access Restricted</h3>
        <p className="text-zinc-500 text-xs mt-2 max-w-sm">
          You do not have permission to view ticket sales for this project.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-white text-base font-bold">Ticket Sales</h3>
            {project.linkedEvent && (
              <button
                onClick={() => {
                  setSelectedEventUuid(project.linkedEventUuid || '');
                  setSearchEventQuery('');
                  setIsChangeEventModalOpen(true);
                }}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0C2540] text-blue-400 border border-blue-500/20 cursor-pointer hover:bg-[#0c2540]/80 transition-all"
              >
                <svg
                  className="w-3 h-3 text-blue-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                <span>{project.linkedEvent}</span>
              </button>
            )}
          </div>
          <p className="text-zinc-550 text-[11px] font-medium mt-1">
            {totalTicketsSold} transactions • Compare revenue against expenses to gauge
            profitability
          </p>
        </div>
        <button
          onClick={handleExportTicketSales}
          className="px-4 py-2 border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
        >
          <svg
            className="w-3.5 h-3.5 text-zinc-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>Export</span>
        </button>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4">
          <div className="flex items-center justify-between text-zinc-550 text-[11px] font-semibold">
            <span>Total Revenue</span>
            <FiTag className="w-3.5 h-3.5" />
          </div>
          <p className="text-emerald-400 text-2xl font-bold mt-2">
            ${ticketRevenue.toLocaleString()}
          </p>
          <p className="text-zinc-550 text-[10px] mt-1">{totalTicketsSold} tickets sold</p>
        </div>

        {ticketTypesSummary.map((t) => (
          <div key={t.ticketType} className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4">
            <div className="flex items-center justify-between text-zinc-550 text-[11px] font-semibold">
              <span>{t.ticketType}</span>
              <FiTag className="w-3.5 h-3.5" />
            </div>
            <p className="text-white text-2xl font-bold mt-2">{t.quantity}</p>
            <p className="text-emerald-450 text-[10px] font-bold mt-1">
              +${t.revenue.toLocaleString()}
            </p>
          </div>
        ))}

        <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4">
          <div className="flex items-center justify-between text-zinc-550 text-[11px] font-semibold">
            <span>Net P&L</span>
            <FiAlertTriangle className="w-3.5 h-3.5" />
          </div>
          <p
            className={`${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-500'} text-2xl font-bold mt-2`}
          >
            {netProfitLoss >= 0 ? '+' : ''}${netProfitLoss.toLocaleString()}
          </p>
          <p className="text-zinc-550 text-[10px] mt-1">
            ${totalSpentExpenses.toLocaleString()} spent
          </p>
        </div>
      </div>

      {/* Revenue vs Expenses progress bar card */}
      <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4">
        <div className="flex items-center justify-between text-xs font-semibold mb-3">
          <span className="text-white">Revenue vs Expenses</span>
          {netProfitLoss < 0 ? (
            <span className="text-rose-500 font-bold">
              Over budget by ${Math.abs(netProfitLoss).toLocaleString()}
            </span>
          ) : (
            <span className="text-emerald-400 font-bold">
              Net positive by ${netProfitLoss.toLocaleString()}
            </span>
          )}
        </div>

        {/* Progress bar visual */}
        <div className="w-full h-2.5 bg-zinc-900 rounded-full overflow-hidden flex">
          {/* Revenue Part */}
          <div
            className="h-full bg-emerald-500"
            style={{
              width: `${(ticketRevenue / (ticketRevenue + totalSpentExpenses)) * 100}%`,
            }}
          />
          {/* Expenses Part */}
          <div
            className="h-full bg-rose-500"
            style={{
              width: `${(totalSpentExpenses / (ticketRevenue + totalSpentExpenses)) * 100}%`,
            }}
          />
        </div>

        {/* Legend labels */}
        <div className="flex items-center justify-between text-[10px] text-zinc-550 mt-3 font-semibold">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>Revenue ${ticketRevenue.toLocaleString()}</span>
          </div>
          <div className="text-zinc-500">
            {ticketTypesSummary.map((t) => `${t.quantity} ${t.ticketType}`).join(' • ')}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rose-500" />
            <span>Expenses ${totalSpentExpenses.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Transactions Table Section */}
      <div className="space-y-4">
        {/* Table Header Filter Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 z-20">
          {/* Left Dropdowns: Ticket type & Sort */}
          <div className="flex items-center gap-3">
            {/* Ticket Type Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActivePopover(activePopover === 'ticket' ? null : 'ticket')}
                className="px-4 py-2 border border-white/5 bg-[#0F0F0F] hover:bg-zinc-900 rounded-full text-zinc-300 hover:text-white text-xs font-bold transition-all cursor-pointer flex items-center gap-2"
              >
                <span>
                  {ticketFilter === 'All'
                    ? `All (${totalTicketsSold})`
                    : (() => {
                        const selected = ticketTypesSummary.find(
                          (t) => t.ticketType === ticketFilter,
                        );
                        return `${ticketFilter} (${selected?.quantity ?? 0})`;
                      })()}
                </span>
                <svg
                  className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${activePopover === 'ticket' ? 'rotate-185' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {activePopover === 'ticket' && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setActivePopover(null)} />
                  <div className="absolute left-0 mt-2 z-30 w-56 bg-[#0E0E0E] border border-white/5 rounded-2xl p-2.5 shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-left space-y-1">
                    <button
                      onClick={() => {
                        setTicketFilter('All');
                        setActivePopover(null);
                      }}
                      className="w-full px-4 py-3 hover:bg-white/[0.04] rounded-xl text-white text-sm font-bold flex items-center justify-between text-left cursor-pointer"
                    >
                      <span>All ({totalTicketsSold})</span>
                      {ticketFilter === 'All' && (
                        <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                          <FiCheck className="w-3 h-3 text-black font-extrabold" />
                        </span>
                      )}
                    </button>
                    {ticketTypesSummary.map((t) => (
                      <button
                        key={t.ticketType}
                        onClick={() => {
                          setTicketFilter(t.ticketType);
                          setActivePopover(null);
                        }}
                        className="w-full px-4 py-3 hover:bg-white/[0.04] rounded-xl text-white text-sm font-bold flex items-center justify-between text-left cursor-pointer"
                      >
                        <span>
                          {t.ticketType} ({t.quantity})
                        </span>
                        {ticketFilter === t.ticketType && (
                          <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                            <FiCheck className="w-3 h-3 text-black font-extrabold" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActivePopover(activePopover === 'sort' ? null : 'sort')}
                className="px-4 py-2 border border-white/5 bg-[#0F0F0F] hover:bg-zinc-900 rounded-full text-zinc-300 hover:text-white text-xs font-bold transition-all cursor-pointer flex items-center gap-2"
              >
                <svg
                  className="w-3.5 h-3.5 text-zinc-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
                <span>
                  {sortBy === 'Newest'
                    ? 'Newest first'
                    : sortBy === 'Oldest'
                      ? 'Oldest first'
                      : sortBy === 'Highest Amount'
                        ? 'Highest amount'
                        : 'Lowest amount'}
                </span>
                <svg
                  className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${activePopover === 'sort' ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {activePopover === 'sort' && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setActivePopover(null)} />
                  <div className="absolute left-0 mt-2 z-30 w-56 bg-[#0E0E0E] border border-white/5 rounded-2xl p-2.5 shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-left space-y-1">
                    {(['Newest', 'Oldest', 'Highest Amount', 'Lowest Amount'] as const).map(
                      (opt) => (
                        <button
                          key={opt}
                          onClick={() => {
                            setSortBy(opt);
                            setActivePopover(null);
                          }}
                          className="w-full px-4 py-3 hover:bg-white/[0.04] rounded-xl text-white text-sm font-bold flex items-center justify-between text-left cursor-pointer"
                        >
                          <span>
                            {opt === 'Newest'
                              ? 'Newest first'
                              : opt === 'Oldest'
                                ? 'Oldest first'
                                : opt === 'Highest Amount'
                                  ? 'Highest amount'
                                  : 'Lowest amount'}
                          </span>
                          {sortBy === opt && (
                            <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                              <FiCheck className="w-3 h-3 text-black font-extrabold" />
                            </span>
                          )}
                        </button>
                      ),
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right search and Advanced Filters menu */}
          <div className="flex items-center gap-2 self-stretch sm:self-auto flex-wrap">
            <div className="relative flex-1 sm:w-64 min-w-[200px]">
              <svg
                className="w-3.5 h-3.5 text-zinc-555 absolute left-3 top-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={ticketSearch}
                onChange={(e) => setTicketSearch(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-white/5 outline-none text-white text-xs rounded-full pl-9 pr-4 py-2 focus:border-white/10 transition-all placeholder:text-zinc-600"
              />
            </div>

            {/* Amount Spent Active Pill */}
            {getAmountFilterLabel() && (
              <div className="bg-[#007AFF] text-white rounded-full px-3.5 py-1.5 text-xs font-bold flex items-center gap-1.5 shadow-md">
                <span>{getAmountFilterLabel()}</span>
                <button
                  onClick={() => {
                    setMinSpend('');
                    setMaxSpend('');
                  }}
                  className="hover:text-blue-100 transition-all cursor-pointer flex items-center justify-center"
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Date Range Active Pill */}
            {getDateFilterLabel() && (
              <div className="bg-[#007AFF] text-white rounded-full px-3.5 py-1.5 text-xs font-bold flex items-center gap-1.5 shadow-md">
                <span>{getDateFilterLabel()}</span>
                <button
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                  className="hover:text-blue-100 transition-all cursor-pointer flex items-center justify-center"
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <div className="relative">
              <button
                onClick={() => setActivePopover(activePopover === 'filter' ? null : 'filter')}
                className={`px-4 py-2 border rounded-full text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
                  activePopover === 'filter' ||
                  activePopover === 'dateRange' ||
                  activePopover === 'amountSpent'
                    ? 'bg-zinc-850 text-white border-white/10'
                    : 'border-white/5 bg-[#0F0F0F] text-zinc-300 hover:text-white'
                }`}
              >
                <svg
                  className="w-3.5 h-3.5 text-zinc-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Filter</span>
                <svg
                  className={`w-3 h-3 text-zinc-400 transition-transform ${
                    activePopover === 'filter' ||
                    activePopover === 'dateRange' ||
                    activePopover === 'amountSpent'
                      ? 'rotate-180'
                      : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Advanced Filters Menu popovers */}
              {(activePopover === 'filter' ||
                activePopover === 'dateRange' ||
                activePopover === 'amountSpent') && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setActivePopover(null)} />

                  {/* SCREEN 1: Main Filter Choices */}
                  {activePopover === 'filter' && (
                    <div className="absolute right-0 mt-2 z-30 w-60 bg-[#0E0E0E] border border-white/5 rounded-2xl p-2.5 shadow-[0_20px_50px_rgba(0,0,0,0.8)] text-left space-y-1">
                      <button
                        onClick={() => {
                          setActivePopover('dateRange');
                          setTempDateFrom(dateFrom);
                          setTempDateTo(dateTo);
                        }}
                        className="w-full px-4 py-3.5 hover:bg-white/[0.04] rounded-xl text-white text-sm font-bold flex items-center justify-between text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <FiCalendar className="w-4 h-4 text-zinc-450" />
                          <span>Date Range</span>
                        </div>
                        <FiChevronRight className="w-4 h-4 text-zinc-555" />
                      </button>

                      <button
                        onClick={() => {
                          setActivePopover('amountSpent');
                          setTempMinSpend(minSpend);
                          setTempMaxSpend(maxSpend);
                          setTempVipOnly(vipOnly);
                        }}
                        className="w-full px-4 py-3.5 hover:bg-white/[0.04] rounded-xl text-white text-sm font-bold flex items-center justify-between text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className="w-4 h-4 text-zinc-450"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                          >
                            <rect x="2" y="6" width="20" height="12" rx="2" />
                            <circle cx="12" cy="12" r="2.5" />
                            <path d="M6 12h.01M18 12h.01" />
                          </svg>
                          <span>Amount Spent</span>
                        </div>
                        <FiChevronRight className="w-4 h-4 text-zinc-555" />
                      </button>
                    </div>
                  )}

                  {/* SCREEN 2: Date Range Form */}
                  {activePopover === 'dateRange' && (
                    <div className="absolute right-0 mt-2 z-30 w-80 bg-[#0B0B0B] border border-white/10 rounded-[24px] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.9)] text-left">
                      <h4 className="text-white text-sm font-bold mb-4 pb-3 border-b border-white/5 flex items-center justify-between">
                        <span>Date Range</span>
                        <button
                          onClick={() => setActivePopover('filter')}
                          className="text-xs text-zinc-450 hover:text-white cursor-pointer font-bold"
                        >
                          Back
                        </button>
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-zinc-400 text-xs font-semibold mb-2">
                            From
                          </label>
                          <input
                            type="date"
                            value={tempDateFrom}
                            max={tempDateTo || undefined}
                            onChange={(e) => setTempDateFrom(e.target.value)}
                            className="w-full bg-zinc-900 border border-white/5 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-white/10 transition-all cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-zinc-400 text-xs font-semibold mb-2">
                            To
                          </label>
                          <input
                            type="date"
                            value={tempDateTo}
                            min={tempDateFrom || undefined}
                            onChange={(e) => setTempDateTo(e.target.value)}
                            className="w-full bg-zinc-900 border border-white/5 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-white/10 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/5">
                        <button
                          onClick={() => {
                            setTempDateFrom('');
                            setTempDateTo('');
                            setDateFrom('');
                            setDateTo('');
                            setActivePopover(null);
                          }}
                          className="flex-1 py-2.5 border border-white/10 hover:bg-white/5 rounded-full text-zinc-400 hover:text-white text-xs font-bold cursor-pointer text-center"
                        >
                          Clear filter
                        </button>
                        <button
                          onClick={() => {
                            if (tempDateFrom && tempDateTo && tempDateTo < tempDateFrom) {
                              toast.error('End date cannot be before start date');
                              return;
                            }
                            setDateFrom(tempDateFrom);
                            setDateTo(tempDateTo);
                            setActivePopover(null);
                          }}
                          className="flex-1 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer text-center"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}

                  {/* SCREEN 3: Amount Spent Form */}
                  {activePopover === 'amountSpent' && (
                    <div className="absolute right-0 mt-2 z-30 w-80 bg-[#0B0B0B] border border-white/10 rounded-[24px] p-5 shadow-[0_20px_50px_rgba(0,0,0,0.9)] text-left">
                      <h4 className="text-white text-sm font-bold mb-4 pb-3 border-b border-white/5 flex items-center justify-between">
                        <span>Amount Spent</span>
                        <button
                          onClick={() => setActivePopover('filter')}
                          className="text-xs text-zinc-450 hover:text-white cursor-pointer font-bold"
                        >
                          Back
                        </button>
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-zinc-400 text-xs font-semibold mb-2">
                            Minimum Spend
                          </label>
                          <input
                            type="number"
                            placeholder="100"
                            value={tempMinSpend}
                            onChange={(e) => setTempMinSpend(e.target.value)}
                            className="w-full bg-zinc-900 border border-white/5 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-white/10 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-zinc-400 text-xs font-semibold mb-2">
                            Maximum Spend
                          </label>
                          <input
                            type="number"
                            placeholder="1,000"
                            value={tempMaxSpend}
                            onChange={(e) => setTempMaxSpend(e.target.value)}
                            className="w-full bg-zinc-900 border border-white/5 outline-none text-white text-xs rounded-xl px-4 py-3 focus:border-white/10 transition-all"
                          />
                        </div>

                        <div className="space-y-2.5 pt-1">
                          <label className="flex items-center gap-2.5 text-xs font-medium text-zinc-350 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={includeRefunded}
                              onChange={(e) => setIncludeRefunded(e.target.checked)}
                              className="w-4 h-4 rounded border-white/5 bg-zinc-900 accent-blue-500"
                            />
                            <span>Include refunded purchases</span>
                          </label>
                          <label className="flex items-center gap-2.5 text-xs font-medium text-zinc-350 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={tempVipOnly}
                              onChange={(e) => setTempVipOnly(e.target.checked)}
                              className="w-4 h-4 rounded border-white/5 bg-zinc-900 accent-blue-500"
                            />
                            <span>VIP customers only</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/5">
                        <button
                          onClick={() => {
                            setTempMinSpend('');
                            setTempMaxSpend('');
                            setTempVipOnly(false);
                            setMinSpend('');
                            setMaxSpend('');
                            setVipOnly(false);
                            setActivePopover(null);
                          }}
                          className="flex-1 py-2.5 border border-white/10 hover:bg-white/5 rounded-full text-zinc-400 hover:text-white text-xs font-bold cursor-pointer text-center"
                        >
                          Clear filter
                        </button>
                        <button
                          onClick={() => {
                            setMinSpend(tempMinSpend);
                            setMaxSpend(tempMaxSpend);
                            setVipOnly(tempVipOnly);
                            setActivePopover(null);
                          }}
                          className="flex-1 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer text-center"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Table Block */}
        <div className="border border-white/5 bg-[#0B0B0B] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
          <div className="overflow-x-auto w-full max-w-full">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/5 text-[9px] font-bold text-zinc-500 uppercase tracking-wider font-mono">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-center">Qty</th>
                  <th className="px-4 py-3">Date & Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-xs">
                {isSalesLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                      <div className="sticky left-0 flex items-center justify-center gap-2 w-[calc(100vw-32px)] sm:w-full">
                        <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                        <span>Loading ticket sales...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTicketTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                      <div className="sticky left-0 flex items-center justify-center w-[calc(100vw-32px)] sm:w-full">
                        No ticket sales found.
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTicketTransactions.map((tx) => {
                    const initials = tx.customerName
                      .split(' ')
                      .map((p: string) => p[0])
                      .join('');
                    const colorsList = [
                      'bg-blue-600',
                      'bg-emerald-600',
                      'bg-purple-600',
                      'bg-pink-600',
                      'bg-amber-600',
                      'bg-rose-600',
                      'bg-teal-600',
                    ];
                    const colorIndex = tx.customerName.charCodeAt(0) % colorsList.length;
                    const avatarColor = colorsList[colorIndex];

                    return (
                      <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${avatarColor}`}
                            >
                              {initials}
                            </div>
                            <span className="text-white font-bold">{tx.customerName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-zinc-450">{tx.customerEmail}</td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${
                              tx.ticketType === 'VIP'
                                ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                                : 'bg-zinc-800 text-zinc-400 border-white/5'
                            }`}
                          >
                            {tx.ticketType}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${
                              tx.paymentMethod === 'Stripe'
                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                : 'bg-emerald-500/10 text-emerald-450 border-emerald-500/20'
                            }`}
                          >
                            {tx.paymentMethod}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right font-semibold text-emerald-400">
                          ${tx.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3.5 text-center text-zinc-300">{tx.quantity}</td>
                        <td className="px-4 py-3.5 text-zinc-500 whitespace-nowrap">
                          {tx.dateTime}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <div className="px-4 py-3 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] font-semibold">
            <span className="text-zinc-550 font-medium">
              {project.uuid
                ? `${ticketSalesData?.results?.length ?? 0} of ${ticketSalesData?.count ?? 0} transactions shown`
                : `${filteredTicketTransactions.length} transactions shown`}
            </span>

            {project.uuid &&
              ticketSalesData &&
              (ticketSalesData.previous || ticketSalesData.next) && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={page <= 1 || isSalesLoading}
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    className="px-3 py-1 border border-white/5 bg-zinc-900/60 text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 transition-all cursor-pointer font-bold rounded-full"
                  >
                    Previous
                  </button>
                  <span className="text-zinc-400">Page {page}</span>
                  <button
                    disabled={!ticketSalesData.next || isSalesLoading}
                    onClick={() => setPage((prev) => prev + 1)}
                    className="px-3 py-1 border border-white/5 bg-zinc-900/60 text-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-800 transition-all cursor-pointer font-bold rounded-full"
                  >
                    Next
                  </button>
                </div>
              )}

            <span className="text-emerald-450 font-bold">
              Total: $
              {project.uuid
                ? Number(ticketSalesData?.summary?.total_revenue ?? 0).toLocaleString()
                : filteredTicketTransactions
                    .reduce((acc, tx) => acc + tx.amount, 0)
                    .toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
